<template>
  <div ref="gridTab" class="grid-tab">
    <div class="grid-tab-toolbar">
      <span>{{ tabTitle }}</span>
      <span class="grid-tab-count">
        <span class="grid-tab-selected-count">{{ $t("common.selected_rows", { count: selectedRowUuids.length }) }} </span>
      </span>
    </div>
    <DxLoadPanel
      :visible="isLoading"
      :target="loadPanelTarget"
      :show-indicator="true"
      :show-pane="true"
      :shading="true"
      shading-color="rgba(255, 255, 255, 0.5)"
    />
    <DxDataGrid
      ref="dataGrid"
      :data-source="rows"
      key-expr="uuid"
      :selected-row-keys="selectedRowUuids"
      :show-borders="true"
      :column-auto-width="true"
      :hover-state-enabled="true"
      :show-column-lines="true"
      :repaint-changes-only="true"
      :row-alternation-enabled="true"
      :column-min-width="50"
      :allow-column-reordering="true"
      @selection-changed="onSelectionChanged"
      :element-attr="gridAttributes"
    >
      <DxSelection mode="multiple" show-check-boxes-mode="always" select-all-mode="allPages" />
      <DxFilterRow :visible="true" />
      <DxPaging :total="3500" :page-size="25" />
      <DxPager
        :visible="true"
        :allowed-page-sizes="pageSizes"
        display-mode="compact"
        :show-page-size-selector="true"
        :show-info="true"
        :show-navigation-buttons="true"
        :info-text="$t('common.page_info')"
      />

      <DxColumn v-for="col in columns" :key="col.field" :data-field="col.field" :caption="col.caption" :data-type="col.dataType" :format="col.format" />

      <DxColumn
        v-if="dataType === 'CONSUM'"
        data-field="row_type"
        :caption="$t('works.parcel_management.col_row_type')"
        cell-template="rowTypeTemplate"
        width="140"
      />
      <template #rowTypeTemplate="{ data }">
        <span>{{ data.data.row_type === 'BATCH_ITEM' ? $t('works.parcel_management.row_type_batch') : $t('works.parcel_management.row_type_sm_consum') }}</span>
      </template>

      <DxColumn
        data-field="target_parcel_uuids"
        :caption="$t('works.parcel_management.target_parcel_column')"
        :allow-filtering="false"
        :allow-sorting="false"
        fixed-position="right"
        :fixed="true"
        cell-template="targetParcelTemplate"
        width="220"
      />
      <template #targetParcelTemplate="{ data }">
        <DxTagBox
          :items="targetParcelOptions"
          value-expr="uuid"
          display-expr="name"
          :value="data.data.target_parcel_uuids"
          :show-selection-controls="true"
          apply-value-mode="instantly"
          @value-changed="(e) => onTargetParcelChanged(data.data, e)"
        />
      </template>
    </DxDataGrid>
  </div>
</template>

<script>
import { DxDataGrid, DxColumn, DxSelection, DxFilterRow, DxPaging, DxPager } from "devextreme-vue/data-grid";
import DxTagBox from "devextreme-vue/tag-box";
import { DxLoadPanel } from "devextreme-vue/load-panel";
import axios from "@/plugins/axios";
import * as endpoints from "@/store/api/endpoints";
import { errorMessage } from "@/helpers/common";
import { mapGetters } from "vuex";

const COLUMNS_BY_TYPE = {
  WORKS: [
    { field: "department_name", captionKey: "works.parcel_management.col_unit" },
    { field: "parcel_name", captionKey: "works.parcel_management.col_parcel_name" },
    { field: "season_name", captionKey: "works.parcel_management.col_season_name" },
    { field: "work_started_at", captionKey: "works.parcel_management.col_date", dataType: "date" },
    { field: "work_name", captionKey: "works.parcel_management.col_work_name" },
    { field: "culture_name", captionKey: "works.parcel_management.col_culture" },
    { field: "duration_hours", captionKey: "works.parcel_management.col_duration" },
    { field: "net_surface_worked", captionKey: "works.parcel_management.col_worked_area", dataType: "number" },
    { field: "person_name", captionKey: "works.parcel_management.col_labor_force" },
    { field: "work_machine_name", captionKey: "works.parcel_management.col_tractor" },
  ],
  CONSUM: [
    { field: "consum_date", captionKey: "works.parcel_management.col_date", dataType: "date" },
    { field: "department_name", captionKey: "works.parcel_management.col_unit" },
    { field: "parcel_name", captionKey: "works.parcel_management.col_parcel_name" },
    { field: "surface", captionKey: "works.parcel_management.col_area", dataType: "number" },
    { field: "culture_name", captionKey: "works.parcel_management.col_culture" },
    { field: "season_name", captionKey: "works.parcel_management.col_season_name" },
    { field: "product_name", captionKey: "works.parcel_management.col_product_name" },
    { field: "warehouse_name", captionKey: "works.parcel_management.col_warehouse" },
    { field: "quantity", captionKey: "works.parcel_management.col_quantity", dataType: "number" },
    { field: "measure_unit_symbol", captionKey: "works.parcel_management.col_measure_unit" },
    { field: "material_cost", captionKey: "works.parcel_management.col_material_cost", dataType: "number" },
  ],
  PRODUCTION: [
    { field: "production_date", captionKey: "works.parcel_management.col_date", dataType: "date" },
    { field: "department_name", captionKey: "works.parcel_management.col_unit" },
    { field: "parcel_name", captionKey: "works.parcel_management.col_parcel_name" },
    { field: "surface", captionKey: "works.parcel_management.col_area", dataType: "number" },
    { field: "culture_name", captionKey: "works.parcel_management.col_culture" },
    { field: "season_name", captionKey: "works.parcel_management.col_season_name" },
    { field: "product_name", captionKey: "works.parcel_management.col_product_name" },
    { field: "warehouse_name", captionKey: "works.parcel_management.col_warehouse" },
    { field: "quantity", captionKey: "works.parcel_management.col_quantity", dataType: "number" },
    { field: "measure_unit_symbol", captionKey: "works.parcel_management.col_measure_unit" },
  ],
};

const ROWS_FIELD_BY_TYPE = {
  WORKS: "works_rows",
  CONSUM: "consum_rows",
  PRODUCTION: "production_rows",
};

export default {
  name: "ParcelManagementDataGridTab",
  components: {
    DxDataGrid,
    DxColumn,
    DxSelection,
    DxFilterRow,
    DxPaging,
    DxPager,
    DxTagBox,
    DxLoadPanel,
  },
  props: {
    formData: {
      type: Object,
      required: true,
    },
    dataType: {
      type: String,
      required: true, // WORKS | CONSUM | PRODUCTION
    },
  },
  data() {
    return {
      rows: [],
      selectedRowUuids: [],
      isLoading: false,
      loadPanelTarget: null,
      pageSizes: [25, 50, 100],
    };
  },
  mounted() {
    this.loadPanelTarget = this.$refs.gridTab;
  },
  computed: {
    ...mapGetters("settings", ["getDxGridDefaultDateFormat"]),
     gridAttributes() {
      return {
        class: "smagria-grid parcel-management-grid",
      };
    },
    columns() {
      return (COLUMNS_BY_TYPE[this.dataType] || []).map((col) => ({
        ...col,
        caption: this.$t(col.captionKey),
        format: col.dataType === "date" ? this.getDxGridDefaultDateFormat : col.format,
      }));
    },
    rowsField() {
      return ROWS_FIELD_BY_TYPE[this.dataType];
    },
    tabTitle() {
      return this.$t(`works.parcel_management.step_${this.dataType.toLowerCase()}`);
    },
    targetParcelOptions() {
      return this.formData.target_parcels || [];
    },
    selectedRowsField() {
      return this.rowsField.replace("_rows", "_selected_row_uuids");
    },
  },
  methods: {
    async onEnter() {
      const sourceParcelUuids = (this.formData.source_parcels || []).map((p) => p.uuid);
      if (!sourceParcelUuids.length) {
        return;
      }

      // Reuse previously loaded rows for this data type if already fetched during this session.
      if (Array.isArray(this.formData[this.rowsField]) && this.formData[this.rowsField].length > 0) {
        this.rows = this.formData[this.rowsField];
        this.selectedRowUuids = this.formData[this.selectedRowsField] || this.rows.map((row) => row.uuid);
        return;
      }

      this.isLoading = true;
      try {
        const response = await axios.get(endpoints.PARCEL_MANAGEMENT_MOVABLE_DATA, {
          params: { data_type: this.dataType, source_parcel_uuids: sourceParcelUuids },
        });

        const targetParcelUuids = (this.formData.target_parcels || []).map((p) => p.uuid);
        const defaultTargets = targetParcelUuids.length === 1 ? targetParcelUuids : [...targetParcelUuids];

        this.rows = (response?.data?.data || []).map((row) => ({
          ...row,
          duration_hours: this.computeDurationHours(row),
          material_cost: this.computeMaterialCost(row),
          target_parcel_uuids: [...defaultTargets],
        }));

        this.selectedRowUuids = this.rows.map((row) => row.uuid);

        this.$emit("update-form-data", { [this.rowsField]: this.rows, [this.selectedRowsField]: this.selectedRowUuids });
      } catch (e) {
        errorMessage(this, this.$t("works.parcel_management.error_loading_data"));
      } finally {
        this.isLoading = false;
      }
    },
    computeDurationHours(row) {
      if (!row?.working_time) {
        return 0;
      }
      // working_time stored as HH:mm time value
      const parts = String(row.working_time).split(":");
      const hours = parseFloat(parts[0] || 0);
      const minutes = parseFloat(parts[1] || 0);
      return Math.round((hours + minutes / 60) * 100) / 100;
    },
    computeMaterialCost(row) {
      if (this.dataType !== "CONSUM") {
        return null;
      }
      return Math.round((parseFloat(row.quantity || 0) * parseFloat(row.unit_price || 0)) * 100) / 100;
    },
    onSelectionChanged(e) {
      this.selectedRowUuids = (e.selectedRowKeys || []).slice();
      this.$emit("update-form-data", { [this.selectedRowsField]: this.selectedRowUuids });
    },
    onTargetParcelChanged(row, e) {
      let value = e.value || [];
      if (row.row_type === "BATCH_ITEM" && value.length > 1) {
        errorMessage(this, this.$t("works.parcel_management.error_batch_single_target"));
        value = [value[value.length - 1]];
      }
      row.target_parcel_uuids = value;
      this.$emit("update-form-data", { [this.rowsField]: this.rows });
    },
    validate() {
      return { error: false };
    },
  },
};
</script>

<style scoped>
.grid-tab {
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  min-height: 0;
  min-width: 0;
  position: relative;
  overflow: hidden;
}

.parcel-management-grid {
  flex: 1 1 auto;
  width: 100%;
  min-height: 0;
  min-width: 0;
}

.grid-tab-toolbar {
  display: flex;
  justify-content: space-between;
  margin-bottom: 12px;
  font-weight: bold;
}

.grid-tab-count {
  color: #666;
  font-weight: normal;
}
</style>
